import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { FileSpec } from '../types';

const ReferenceContainer = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const SpecInfo = styled.div<{ bgColor: string; borderColor: string }>`
  background: ${props => props.bgColor};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid ${props => props.borderColor};
`;

const SpecTitle = styled.h3<{ color: string }>`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 16px;
`;

const SpecDetail = styled.p<{ color: string }>`
  font-size: 14px;
  color: ${props => props.color};
  margin-bottom: 8px;
  
  strong {
    color: ${props => props.color};
  }
`;

const RecordSection = styled.div<{ borderColor: string }>`
  border: 1px solid ${props => props.borderColor};
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const RecordHeader = styled.div<{ bgColor: string }>`
  background: ${props => props.bgColor};
  padding: 12px 16px;
`;

const RecordTitle = styled.h4<{ color: string }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.color};
  margin: 0;
`;

const FieldList = styled.div`
  padding: 16px;
`;

const FieldItem = styled.div<{ borderColor: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.borderColor};
  
  &:last-child {
    border-bottom: none;
  }
`;

const UsageBadge = styled.span<{ bgColor: string; textColor: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  white-space: nowrap;
`;

const FieldInfo = styled.div`
  flex: 1;
`;

const FieldName = styled.span<{ color: string }>`
  font-weight: 500;
  color: ${props => props.color};
`;

const FieldMeta = styled.span<{ color: string }>`
  font-size: 13px;
  color: ${props => props.color};
  margin-left: 8px;
`;

interface SpecReferenceProps {
  spec: FileSpec;
}

export const SpecReference: React.FC<SpecReferenceProps> = ({ spec }) => {
  const theme = useTheme();
  
  const getUsageStyle = (usage: string) => {
    switch (usage) {
      case 'Required': return { bg: `${theme.colors.error}20`, text: theme.colors.error, label: 'Required' };
      case 'Conditional': return { bg: `${theme.colors.warning}20`, text: theme.colors.warning, label: 'Conditional' };
      default: return { bg: '#e5e7eb', text: '#6b7280', label: 'Optional' };
    }
  };
  
  return (
    <ReferenceContainer>
      <SpecInfo bgColor={`${theme.colors.optumBlue}08`} borderColor={theme.colors.border}>
        <SpecTitle color={theme.colors.optumBlue}>{spec.description}</SpecTitle>
        <SpecDetail color={theme.colors.textSecondary}>
          <strong>Specification:</strong>{' '}
          {spec.specPdfUrl ? (
            <a
              href={spec.specPdfUrl}
              download
              style={{ color: theme.colors.optumBlue, textDecoration: 'underline' }}
            >
              {spec.name}
            </a>
          ) : (
            spec.name
          )}
        </SpecDetail>
        <SpecDetail color={theme.colors.textSecondary}>
          <strong>Version:</strong> {spec.version}
        </SpecDetail>
        <SpecDetail color={theme.colors.textSecondary}>
          <strong>Record Length:</strong> {spec.recordLength} characters (fixed-width)
        </SpecDetail>
      </SpecInfo>
      
      {Object.entries(spec.records).map(([recordId, recordSpec]) => (
        <RecordSection key={recordId} borderColor={theme.colors.border}>
          <RecordHeader bgColor={theme.colors.bgLight}>
            <RecordTitle color={theme.colors.optumBlue}>
              {recordId} - {recordSpec.name}
            </RecordTitle>
          </RecordHeader>
          <FieldList>
            {recordSpec.fields.map((field, index) => {
              const usageStyle = getUsageStyle(field.usage);
              const valuesText = field.values 
                ? ` | Values: ${field.values.join(', ')}`
                : '';
              
              return (
                <FieldItem key={index} borderColor={theme.colors.borderLight}>
                  <UsageBadge bgColor={usageStyle.bg} textColor={usageStyle.text}>
                    {usageStyle.label}
                  </UsageBadge>
                  <FieldInfo>
                    <FieldName color={theme.colors.textPrimary}>{field.name}</FieldName>
                    <FieldMeta color={theme.colors.textSecondary}>
                      ({field.dataType}, length: {field.length}, pos: {field.start}-{field.end}){valuesText}
                    </FieldMeta>
                  </FieldInfo>
                </FieldItem>
              );
            })}
          </FieldList>
        </RecordSection>
      ))}
    </ReferenceContainer>
  );
};
